
import { useState, useCallback, useEffect, startTransition } from 'react';
import { Device } from '@twilio/voice-sdk';
import { useToast } from './use-toast';
import { initializeDevice, getDevice, getDeviceStatus, destroyDevice } from '@/services/twilio/voice/deviceManager';
import { checkMicrophonePermission } from '@/services/twilio/permissions';

export interface TwilioPermissions {
  micPermission: PermissionState | null;
  isAudioSetup: boolean;
  isInitializingDevices: boolean;
  requestPermissions: () => Promise<boolean>;
  device: Device | null;
}

export const useTwilioPermissions = (): TwilioPermissions => {
  const [micPermission, setMicPermission] = useState<PermissionState | null>(null);
  const [isAudioSetup, setIsAudioSetup] = useState(false);
  const [isInitializingDevices, setIsInitializingDevices] = useState(false);
  const [device, setDevice] = useState<Device | null>(null);
  const { toast } = useToast();

  // Check for existing permissions and device state on mount
  const checkExistingState = useCallback(async () => {
    try {
      // Check microphone permission status first
      const permissionState = await checkMicrophonePermission();
      startTransition(() => {
        setMicPermission(permissionState);
      });
      
      // Check for existing device from device manager
      const existingDevice = getDevice();
      if (existingDevice) {
        startTransition(() => {
          setDevice(existingDevice);
        });
        const deviceStatus = getDeviceStatus();
        startTransition(() => {
          setIsAudioSetup(deviceStatus === 'registered');
        });
        console.log('🎤 Found existing device with status:', deviceStatus);
      } else if (permissionState === 'granted') {
        // If permission is granted but no device, we might need to initialize
        console.log('🎤 Permission granted but no device found');
      }
    } catch (error) {
      console.error('Error checking existing state:', error);
    }
  }, []);

  const requestPermissions = useCallback(async (): Promise<boolean> => {
    // Check current permission state first
    const currentPermission = await checkMicrophonePermission();
    startTransition(() => {
      setMicPermission(currentPermission);
    });
    
    // If already granted and device exists, no need to reinitialize
    if (currentPermission === 'granted' && device && getDeviceStatus() === 'registered') {
      console.log('🎤 Permissions already granted and device ready');
      startTransition(() => {
        setIsAudioSetup(true);
      });
      return true;
    }
    
    startTransition(() => {
      setIsInitializingDevices(true);
    });
    
    try {
      // Only request permission if not already granted
      if (currentPermission !== 'granted') {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        stream.getTracks().forEach(track => track.stop()); 
        startTransition(() => {
          setMicPermission('granted');
        });
        console.log('🎤 Microphone permission granted.');
      }

      // Initialize Twilio Device if not already done or if previous initialization failed
      if (!device || getDeviceStatus() !== 'registered') {
        console.log('🎤 Initializing Twilio Device...');
        const deviceResult = await initializeDevice();
        
        if (deviceResult.success && deviceResult.device) {
          startTransition(() => {
            setDevice(deviceResult.device);
            setIsAudioSetup(true);
            setIsInitializingDevices(false);
          });
          console.log('✅ Twilio Device initialized successfully');
          return true;
        } else {
          console.error('❌ Failed to initialize Twilio Device:', deviceResult.error);
          toast({
            title: 'Device Setup Failed',
            description: deviceResult.error || 'Could not initialize Twilio services.',
            variant: 'destructive',
          });
          startTransition(() => {
            setIsInitializingDevices(false);
          });
          return false;
        }
      } else {
        console.log('✅ Device already initialized and ready');
        startTransition(() => {
          setIsAudioSetup(true);
          setIsInitializingDevices(false);
        });
        return true;
      }
    } catch (error) {
      console.error('❌ Permission or device initialization error:', error);
      startTransition(() => {
        setMicPermission('denied');
        setIsInitializingDevices(false);
      });
      toast({
        title: 'Microphone Access Denied',
        description: 'Please allow microphone access in your browser settings to make calls.',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, device]);

  useEffect(() => {
    // Initialize state check on mount
    startTransition(() => {
      checkExistingState();
    });

    // Set up permission change listener
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'microphone' as PermissionName }).then((permissionStatus) => {
        startTransition(() => {
          setMicPermission(permissionStatus.state);
        });
        permissionStatus.onchange = () => {
          startTransition(() => {
            setMicPermission(permissionStatus.state);
            if (permissionStatus.state === 'denied') {
              setIsAudioSetup(false);
            }
          });
          console.log('🎤 Microphone permission revoked');
        };
      });
    }

    return () => {
      // Clean up is handled by the device manager
      console.log("useTwilioPermissions cleanup - device manager handles destruction");
    };
  }, [checkExistingState]);

  return { micPermission, isAudioSetup, isInitializingDevices, requestPermissions, device };
};
